import { TireSeason, TireSeasonLabels } from '@navio/nsdk';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import {
  encodeStorageLabel,
  formatLicensePlate,
  formatPrintedLicensePlate,
  StorageLabelKind,
  TIRE_SEASON_ICON
} from 'noddi-util';
import QRCode from 'react-qr-code';
import { QRCodeLabel } from './QRCodeLabel';
import { useStorageLabelDesignStore } from './storageLabelDesignStore';

const environment = import.meta.env.MODE;

// Winter prints the 3PMSF mark from the tire sidewall instead of the app's snowflake.
const LABEL_SEASON_ICON = {
  ...TIRE_SEASON_ICON,
  [TireSeason.WINTER]: 'ThreePeakMountainSnowflake'
} as const satisfies Record<TireSeason, NoddiIconName>;

export type LabelForTiresProps = {
  licensePlate: string;
  carMake: string;
  carModel: string;
  season: TireSeason | null | undefined;
  /** Printed only on the previous label design. */
  ownerName: string;
  storageObjectGroupIdentifier: string;
};

// The page fills one sheet of the label printer's 62 x 100 mm paper, printed landscape; the preview shows
// it at the paper's proportions on screen.
const LAYOUT_CLASS = {
  page: 'h-screen break-inside-avoid break-after-page',
  preview: 'aspect-[100/62] w-full'
} as const;

export type LabelForTiresLayout = keyof typeof LAYOUT_CLASS;

/**
 * Sized in cqh (percent of the label's height), so the printed page and the on-screen preview are the same
 * label. The code under the QR and the season name share one baseline.
 */
export const LabelForTires = ({
  layout,
  ownerName,
  ...props
}: LabelForTiresProps & { layout: LabelForTiresLayout }) => {
  const printsNewDesign: boolean = useStorageLabelDesignStore((state) => state.printsNewDesign);
  return printsNewDesign ? (
    <SeasonMarkLabel layout={layout} {...props} />
  ) : (
    <PreviousLabel ownerName={ownerName} {...props} />
  );
};

const SeasonMarkLabel = ({
  layout,
  licensePlate,
  carMake,
  carModel,
  season,
  storageObjectGroupIdentifier
}: Omit<LabelForTiresProps, 'ownerName'> & { layout: LabelForTiresLayout }) => (
  <div className={cn(LAYOUT_CLASS[layout], '[container-type:size]')}>
    <div className='flex size-full gap-[5cqh] px-[8cqh] py-[6cqh] text-black'>
      <div className='flex h-full shrink-0 flex-col items-center justify-between'>
        <QRCode
          value={encodeStorageLabel({
            kind: StorageLabelKind.WHEEL_SET,
            identifier: storageObjectGroupIdentifier,
            environment
          })}
          size={256}
          viewBox='0 0 256 256'
          level='L'
          className='size-[81cqh]'
        />
        <p className='text-[4.5cqh] leading-none [text-box:trim-both_cap_alphabetic]'>{storageObjectGroupIdentifier}</p>
      </div>
      <div className='flex min-w-0 flex-1 flex-col justify-between'>
        <div className='flex flex-col gap-[3cqh]'>
          <p className='rounded-[2cqh] border-[0.8cqh] border-current py-[1.5cqh] text-center indent-[0.1em] text-[10cqh] leading-none font-bold tracking-widest [font-kerning:none]'>
            {formatPrintedLicensePlate(licensePlate)}
          </p>
          <div className='flex flex-col gap-[1cqh]'>
            <p className='line-clamp-2 text-[9cqh] leading-tight font-bold'>{carMake}</p>
            <p className='line-clamp-2 text-[6.5cqh] leading-tight'>{carModel}</p>
          </div>
        </div>
        {season && (
          <div className='flex flex-col items-center gap-[1cqh] self-end'>
            <NoddiIcon name={LABEL_SEASON_ICON[season]} strokeWidth={1.1} className='size-[24cqh]' />
            <p className='text-[8cqh] leading-none uppercase [text-box:trim-both_cap_alphabetic]'>
              {TireSeasonLabels[season]}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// The label as it printed before the season mark: spacing is calibrated for Brother printer labels.
const PreviousLabel = ({
  licensePlate,
  carMake,
  carModel,
  season,
  ownerName,
  storageObjectGroupIdentifier
}: LabelForTiresProps) => (
  <div className='flex items-center justify-center gap-6'>
    <QRCodeLabel storageObjectGroupIdentifier={storageObjectGroupIdentifier} />
    <div className='flex w-5/12 flex-col gap-2'>
      <h3 className='font-bold'>{formatLicensePlate(licensePlate)}</h3>
      <div className='flex flex-col gap-1'>
        <p className='font-bold'>{`${carMake} - ${carModel}`}</p>
        <p>{season ? TireSeasonLabels[season] : 'Unknown'} tire</p>
        <p>{ownerName}</p>
      </div>
    </div>
  </div>
);
